import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigateToPost } from "@hooks/useNavigateToPost"
import { SmartDateFormat, formatPlaylistDuration } from "@util/dates"
import { PLAYLIST_PLACEHOLDER_IMAGE_URL } from "@util/constants"
import { PauseCircle, PlayCircle, PlusCircle } from "react-bootstrap-icons"
import { Spinner } from "../../shared/Spinner"
import { RequestError } from "../../shared/RequestError"
import { useDispatch, useSelector } from "react-redux"
import {
  prependAndPlay,
  pushToQueue,
  playOrPause,
} from "../../../stores/mediaPlayerSlice"
import { openSongActions } from "../../../stores/songActionsSlice"
import { PostListItemStyles } from "../constants"
import { usePost } from "@hooks/usePost"
import { ShareUrlButton } from "../../ShareUrlButton"
import { ShowHideButton } from "../../shared/Button"
import RelatedPosts from "../../RelatedPosts"
import clsx from "clsx"
import PremiereHeader from "../PremiereHeader"
import styles from "../styles.module.css"
import { PlaylistListItemParams, PlaylistSong } from "@models/playlists"
import CommentSectionPreview from "../../comments/CommentSectionPreview"

const ICON_SIZE = "1.6rem"
const MARGIN_SPACING = "0rem 0.5rem 0rem 0"

export function usePlaylistDetails(playlistId: number) {
  const playlistDetailData = usePost(playlistId, "songs")
  return {
    playlistDetailData,
    playlistDetails: playlistDetailData?.data?.post,
  }
}

export function getPlaylistImage(playlist: any) {
  return playlist.external_photo_src || PLAYLIST_PLACEHOLDER_IMAGE_URL
}

export function sortedSongs(playlistDetails: any, playlist: any) {
  if (!playlistDetails?.songs) return []
  return [...playlistDetails.songs].map((song: PlaylistSong) => ({
    ...song,
    playlist_name: playlistDetails.title,
    playlist_path: playlist.nada_path,
    cover_image_small: playlistDetails.external_photo_src,
    playlistId: playlist.id,
  }))
}

const PlaylistItem: React.FC<PlaylistListItemParams> = ({
  playlist,
  updateStatus,
  adminMode = false,
}) => {
  const [postStatus, setPostStatus] = useState(playlist.status)
  const { playlistDetailData, playlistDetails } = usePlaylistDetails(
    playlist.id,
  )
  const navigateToPostScreen = useNavigateToPost(playlist.nada_path)

  const dispatch = useDispatch()
  const currentMediaItem = useSelector(
    (state: any) => state.mediaPlayer.value.currentMediaItem,
  )
  const playerState = useSelector(
    (state: any) => state.mediaPlayer.value.playerState,
  )
  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.playlistId === playlist.id
  const isCurrentlyQueued =
    playerState === "paused" && currentMediaItem?.playlistId === playlist.id

  const onPlayClick = (e: React.MouseEvent) => {
    dispatch(prependAndPlay(sortedSongs(playlistDetails, playlist)))
  }

  const onPlayOrPauseClick = (e: React.MouseEvent) => {
    dispatch(playOrPause({}))
  }

  const onAddClick = (e: React.MouseEvent) => {
    dispatch(pushToQueue(sortedSongs(playlistDetails, playlist)))
  }

  return (
    <div className="grid grid-cols-12">
      <div className={PostListItemStyles.contentDiv}>
        <img
          src={getPlaylistImage(playlist)}
          alt={playlist.title}
          className="w-full cursor-pointer"
          style={{ aspectRatio: "1/1" }}
          onClick={navigateToPostScreen}
        />
      </div>
      <div className={PostListItemStyles.metaDiv}>
        <PremiereHeader post={playlist} size="small" />
        <div className="flex">
          <div className="flex flex-none">
            {isCurrentlyPlaying ? (
              <PauseCircle
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={onPlayOrPauseClick}
              />
            ) : (
              <PlayCircle
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={isCurrentlyQueued ? onPlayOrPauseClick : onPlayClick}
              />
            )}
            <PlusCircle
              className="cursor-pointer"
              style={{
                minWidth: ICON_SIZE,
                width: ICON_SIZE,
                height: ICON_SIZE,
                margin: MARGIN_SPACING,
              }}
              onClick={onAddClick}
            />
          </div>

          <div className="flex items-start justify-between flex-grow">
            <h2
              className={clsx(
                PostListItemStyles.header,
                "inline mr-2 cursor-pointer",
                playlist.is_bn_premiere && styles.premiereTitle,
              )}
              dangerouslySetInnerHTML={{ __html: playlist.title }}
              onClick={navigateToPostScreen}
            />
            <ShareUrlButton nadaUrl={playlist.nada_url} renderMode="icon" />
          </div>
        </div>
        <h3>
          <Link
            className={PostListItemStyles.bandName}
            to={playlist.local_path}
          >
            {playlist.band_name}
          </Link>
        </h3>
        <div className="text-xs">
          <div>
            Created&nbsp;
            {SmartDateFormat({
              dateStr: playlist.published_at,
              includeTime: false,
            })}
          </div>
        </div>
        {playlist.description && (
          <div className={PostListItemStyles.description}>
            {playlist.description}
          </div>
        )}
        <div className="text-xs text-gray-500">
          {playlistDetails?.songs?.length || 0} songs
          {playlistDetails?.songs && (() => {
            const dur = formatPlaylistDuration(playlistDetails.songs)
            return dur ? `, ${dur}` : null
          })()}
        </div>
        <div className="mt-2">
          {playlistDetailData.status === "loading" ? (
            <Spinner className="text-gray-500" />
          ) : playlistDetailData.status === "error" ? (
            <RequestError error={playlistDetailData.error} />
          ) : (
            <>
              {playlistDetails?.songs?.length > 0 ? (
                <>
                  <ul className="text-sm text-black">
                    {sortedSongs(playlistDetails, playlist)
                      .slice(0, 5)
                      .map((song: PlaylistSong, index: number) => (
                        <li key={song.id} className="mb-1">
                          <div className="flex">
                            <div className="flex flex-none">
                              <PlayCircle
                                className="cursor-pointer"
                                style={{
                                  minWidth: ICON_SIZE,
                                  width: ICON_SIZE,
                                  height: ICON_SIZE,
                                  margin: MARGIN_SPACING,
                                }}
                                onClick={() => dispatch(prependAndPlay([song]))}
                              />
                              <PlusCircle
                                className="cursor-pointer"
                                style={{
                                  minWidth: ICON_SIZE,
                                  width: ICON_SIZE,
                                  height: ICON_SIZE,
                                  margin: MARGIN_SPACING,
                                }}
                                onClick={() => dispatch(openSongActions(song))}
                              />
                            </div>
                            <div className="flex-grow">
                              <span className="font-bold">{index + 1}.</span>
                              &nbsp;
                              <span
                                className={
                                  song.streaming_url
                                    ? "text-gray-700"
                                    : "text-gray-400"
                                }
                              >
                                {song.title}
                              </span>
                              <Link
                                to={song.local_path}
                                className="italic pl-1 text-gray-500 text-xs"
                              >
                                {song.band_name}
                              </Link>
                            </div>
                          </div>
                        </li>
                      ))}
                    {playlistDetails.songs.length > 5 && (
                      <li className="text-xs text-gray-500 mt-2">
                        +{playlistDetails.songs.length - 5} more songs
                      </li>
                    )}
                  </ul>
                </>
              ) : (
                <div className="text-sm text-gray-500">
                  No songs in this playlist
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-4">
          <RelatedPosts postId={playlist.id} />
        </div>
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
        <div className="mt-6">
          <CommentSectionPreview
            postId={playlist.id}
            postPath={playlist.nada_url}
            className="mb-4"
          />
        </div>
      </div>
    </div>
  )
}

export default PlaylistItem
